/**
 * Yearly costs - w2_16 (Worthly 2.0, Premium, in Your plans).
 *
 * The costs that only come now and then: car insurance, gifts, flights home.
 * Worthly spreads them into one amount to set aside each month, shows which
 * months are heavy, and, when a cost is linked to a sinking fund, whether
 * that fund already has enough.
 */
import { useMemo, useState } from "react";
import {
  K, tintAt, num, toText, money, wholeMoney, shortDate,
  PlanPage, PlanSheet, Label, Help, MoneyInput, BlackButton, QuietButton, Chip, Card, Note, Bar, EmojiSquare, inputStyle,
} from "./planKit";
import { FREQS, intervalOf, nextDue, perMonth, perYear, nextTwelve, bigMonth, readiness, freqLabel } from "../../utils/yearlyCosts";

const EMOJIS = ['\u{1F697}', '\u{1F381}', '✈️', '\u{1F9FE}', '\u{1F382}', '\u{1F4BB}', '\u{1F3E0}', '\u{1F4F1}', '\u{1F393}', '\u{1F43E}', '\u{1F48A}', '\u{1F384}'];
const guessEmoji = (name) => {
  const t = String(name || '').toLowerCase();
  const pairs = [
    [/car|auto|licen|plate|tire/, '\u{1F697}'], [/gift|present/, '\u{1F381}'], [/christmas|holiday/, '\u{1F384}'],
    [/flight|trip|travel|vacation|holiday/, '✈️'], [/birthday|bday/, '\u{1F382}'], [/tax|account|cra|irs/, '\u{1F9FE}'],
    [/apple|icloud|software|app|laptop|computer|domain/, '\u{1F4BB}'], [/home|house|condo|property/, '\u{1F3E0}'], [/phone/, '\u{1F4F1}'],
    [/school|tuition|course/, '\u{1F393}'], [/pet|vet|dog|cat/, '\u{1F43E}'], [/dentist|doctor|glasses|health/, '\u{1F48A}'],
    [/hair|braid|salon|nail/, '\u{1F487}\u{1F3FE}‍♀️'], [/insurance/, '\u{1F6E1}️'], [/subscri|membership|costco|amazon|prime/, '\u{1F501}'],
  ];
  const hit = pairs.find(([re]) => re.test(t));
  return hit ? hit[1] : '\u{1F4C6}';
};
const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
const monthLong = (d) => d.toLocaleDateString('en-US', { month: 'long' });
const monthLetter = (d) => d.toLocaleDateString('en-US', { month: 'narrow' });

export default function YearlyCosts({ costs = [], funds = [], onSave, onBack }) {
  const [view, setView] = useState('soon');
  const [edit, setEdit] = useState(null); // null | 'new' | cost
  const today = new Date();
  const list = useMemo(() => (costs || []).map((c, i) => ({ ...c, _i: i, _due: nextDue(c, today) })), [costs]); // eslint-disable-line react-hooks/exhaustive-deps
  const upcoming = list.filter((c) => c._due).sort((a, b) => a._due - b._due);
  const done = list.filter((c) => !c._due);
  const monthly = list.reduce((s, c) => s + perMonth(c, today), 0);
  const yearly = list.reduce((s, c) => s + perYear(c, today), 0);
  const months = nextTwelve(costs, today);
  const maxM = Math.max(1, ...months.map((m) => m.total));
  const big = bigMonth(months);
  const bigDate = big ? months[big.month].date : null;

  const save = (entry) => {
    const next = (costs || []).slice();
    const at = next.findIndex((c) => c.id === entry.id);
    if (at >= 0) next[at] = entry; else next.push(entry);
    onSave(next);
    setEdit(null);
  };
  const remove = (id) => { onSave((costs || []).filter((c) => c.id !== id)); setEdit(null); };

  const row = (c, i, showBar) => {
    const t = tintAt(c._i);
    const r = readiness(c, funds);
    return (
      <button key={c.id || i} type="button" onClick={() => setEdit(c)} className="flex items-center w-full text-left" style={{ gap: 12, padding: '11px 0', background: 'none', border: 'none', borderTop: i ? `1px solid ${K.line}` : 'none', fontFamily: 'inherit', opacity: c._due ? 1 : 0.5 }}>
        <EmojiSquare emoji={c.emoji || guessEmoji(c.name)} bg={t.bg} size={42} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="truncate" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: K.ink }}>{c.name}</span>
          <span style={{ display: 'block', fontSize: 12.5, color: K.soft, marginTop: 1 }}>
            {c._due ? shortDate(c._due) : 'Done'} · {freqLabel(c)}
          </span>
          {showBar && r && <Bar pct={c.amount > 0 ? (Math.min(r.has, c.amount) / c.amount) * 100 : 0} color={r.ready ? K.mintBar : t.bar} height={5} style={{ marginTop: 7, maxWidth: 130 }} />}
        </span>
        <span style={{ textAlign: 'right', flexShrink: 0 }}>
          <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: K.ink }}>{wholeMoney(num(c.amount))}</span>
          <span style={{ display: 'block', fontSize: 11.5, marginTop: 1, color: r ? (r.ready ? K.mintT : K.soft) : K.soft }}>
            {!c._due ? '' : r ? (r.ready ? 'Ready' : `${wholeMoney(r.toGo)} to go`) : `${wholeMoney(perMonth(c, today))} / mo`}
          </span>
        </span>
      </button>
    );
  };

  // "All year": grouped by the month each one is next due.
  const groups = [];
  upcoming.forEach((c) => {
    const label = c._due.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const g = groups.find((x) => x.label === label);
    if (g) g.items.push(c); else groups.push({ label, items: [c] });
  });

  return (
    <PlanPage onBack={onBack} title={'Yearly costs \u{1F4C6}'} sub="The ones that only show up now and then" testid="yearly-costs-page">
      {list.length === 0 ? (
        <Card style={{ padding: 18 }}>
          <div style={{ fontSize: 16.5, fontWeight: 600, color: K.ink }}>Add your first one</div>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: K.soft, lineHeight: 1.5 }}>
            Things like car insurance, holiday gifts, flights home or a yearly subscription. Worthly works out how much to put aside each month so they never catch you off guard!
          </p>
          <div className="flex flex-wrap" style={{ gap: 6, marginTop: 12 }}>
            {['\u{1F697} Car insurance', '\u{1F381} Gifts', '✈️ Flights', '\u{1F9FE} Accountant'].map((x) => (
              <span key={x} style={{ fontSize: 12.5, fontWeight: 600, color: K.lilT, background: K.lil, borderRadius: 999, padding: '5px 10px' }}>{x}</span>
            ))}
          </div>
        </Card>
      ) : (
        <>
          <Card style={{ padding: '16px 16px 14px' }} testid="yearly-costs-summary">
            <div style={{ fontSize: 12.5, color: K.soft }}>Set aside each month</div>
            <div className="flex items-baseline justify-between" style={{ gap: 10, marginTop: 2 }}>
              <span style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.9px', color: K.ink }}>{wholeMoney(monthly)}</span>
              <span style={{ fontSize: 12.5, color: K.soft }}>{wholeMoney(yearly)} a year</span>
            </div>
            <div className="flex items-end" style={{ gap: 4, height: 70, marginTop: 14 }}>
              {months.map((m, i) => {
                const isBig = big && big.month === i;
                return (
                  <div key={i} className="flex flex-col items-center justify-end" style={{ flex: 1, height: '100%', gap: 4 }}>
                    <div style={{ width: '100%', borderRadius: '7px 7px 3px 3px', height: m.total > 0 ? Math.max(8, (m.total / maxM) * 100) + '%' : 4, background: isBig ? K.pinkBar : i === 0 ? K.lilDot : m.total > 0 ? K.lilBar : K.track }} />
                    <span style={{ fontSize: 10, fontWeight: i === 0 ? 600 : 500, color: i === 0 ? K.lilT : K.faint }}>{monthLetter(m.date)}</span>
                  </div>
                );
              })}
            </div>
            {big && (
              <Note tone="pink" style={{ marginTop: 12, fontSize: 13 }}>
                {monthLong(bigDate)} is your big one: {wholeMoney(big.total)} for {big.names.slice(0, 2).join(' and ')}{big.names.length > 2 ? ' and more' : ''}.
              </Note>
            )}
          </Card>

          <div className="flex" style={{ gap: 4, background: '#FFFFFF', borderRadius: 999, padding: 4, marginTop: 14 }}>
            {[['soon', 'Coming up'], ['all', 'All year']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setView(k)} style={{ flex: 1, border: 0, borderRadius: 999, padding: '8px 0', fontSize: 13, fontWeight: 600, background: view === k ? K.btn : 'transparent', color: view === k ? '#FFFFFF' : K.soft, fontFamily: 'inherit' }}>{l}</button>
            ))}
          </div>

          {view === 'soon' ? (
            upcoming.length ? (
              <Card style={{ padding: '4px 16px' }}>{upcoming.slice(0, 5).map((c, i) => row(c, i, true))}</Card>
            ) : (
              <Card><span style={{ fontSize: 14, color: K.soft }}>Nothing coming up.</span></Card>
            )
          ) : (
            <>
              {groups.map((g) => (
                <div key={g.label}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: K.soft, margin: '18px 4px 0' }}>{g.label}</div>
                  <Card style={{ padding: '4px 16px', marginTop: 8 }}>{g.items.map((c, i) => row(c, i, false))}</Card>
                </div>
              ))}
              {done.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: K.soft, margin: '18px 4px 0' }}>Done</div>
                  <Card style={{ padding: '4px 16px', marginTop: 8 }}>{done.map((c, i) => row(c, i, false))}</Card>
                </div>
              )}
            </>
          )}
        </>
      )}
      <BlackButton onClick={() => setEdit('new')}>Add a yearly cost</BlackButton>

      {edit && (
        <CostSheet
          cost={edit === 'new' ? null : edit}
          funds={funds}
          onClose={() => setEdit(null)}
          onSave={save}
          onDelete={edit === 'new' ? null : () => remove(edit.id)}
        />
      )}
    </PlanPage>
  );
}

function CostSheet({ cost, funds, onClose, onSave, onDelete }) {
  const [name, setName] = useState(cost ? cost.name || '' : '');
  const [emoji, setEmoji] = useState(cost ? cost.emoji || '' : '');
  const [amount, setAmount] = useState(cost ? toText(cost.amount) : '');
  const [date, setDate] = useState(cost ? String(cost.next_date || '').slice(0, 10) : '');
  const [every, setEvery] = useState(cost ? cost.every || 'year' : 'year');
  const [months, setMonths] = useState(cost && cost.months ? String(cost.months) : '');
  const [fundId, setFundId] = useState(cost && cost.fund_id != null ? String(cost.fund_id) : '');
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const draft = { name, amount: num(amount), next_date: date, every, months: num(months), fund_id: fundId || null };
  const due = date ? nextDue(draft) : null;
  const pm = date && num(amount) > 0 ? perMonth(draft) : 0;
  const r = readiness(draft, funds);
  const shownEmoji = emoji || guessEmoji(name);

  const submit = () => {
    if (!name.trim()) { setError('Add what this cost is.'); return; }
    if (num(amount) <= 0) { setError('Add how much it costs.'); return; }
    if (!date) { setError('Add when it’s next due.'); return; }
    if (every === 'custom' && !(num(months) >= 1)) { setError('Add how many months between each one.'); return; }
    onSave({
      id: cost ? cost.id : newId(),
      name: name.trim(),
      emoji: emoji || '',
      amount: Math.round(num(amount) * 100) / 100,
      next_date: date,
      every,
      months: every === 'custom' ? Math.round(num(months)) : null,
      fund_id: fundId || null,
    });
  };

  return (
    <PlanSheet title={cost ? 'Edit yearly cost' : 'New yearly cost'} onClose={onClose}>
      <Label>What is it?</Label>
      <input type="text" value={name} onChange={(e) => { setName(e.target.value); setError(''); }} placeholder="Car insurance" style={inputStyle} />
      <div className="flex" style={{ gap: 7, marginTop: 10, overflowX: 'auto', paddingBottom: 2 }}>
        <button type="button" onClick={() => setEmoji('')} style={{ flexShrink: 0, height: 40, borderRadius: 14, border: 0, padding: '0 11px', fontSize: 12.5, fontWeight: 600, background: !emoji ? K.lil : K.field, color: !emoji ? K.lilT : K.soft, boxShadow: !emoji ? `inset 0 0 0 2px ${K.lilBar}` : 'none', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Pick for me {guessEmoji(name)}</button>
        {EMOJIS.map((e) => (
          <button key={e} type="button" onClick={() => setEmoji(e)} style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 14, border: 0, fontSize: 18, background: emoji === e ? K.lil : K.field, boxShadow: emoji === e ? `inset 0 0 0 2px ${K.lilBar}` : 'none' }}>{e}</button>
        ))}
      </div>

      <div className="flex" style={{ gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Label>Amount</Label>
          <MoneyInput value={amount} onChange={(v) => { setAmount(v); setError(''); }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Label>Next one</Label>
          <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setError(''); }} style={{ ...inputStyle, appearance: 'none', WebkitAppearance: 'none' }} />
        </div>
      </div>

      <Label>How often</Label>
      <div className="flex flex-wrap" style={{ gap: 7 }}>
        {FREQS.map((f) => <Chip key={f.k} on={every === f.k} onClick={() => { setEvery(f.k); setError(''); }}>{f.label}</Chip>)}
      </div>
      {every === 'custom' && (
        <div className="flex items-center" style={{ gap: 10, marginTop: 10 }}>
          <span style={{ fontSize: 14, color: K.soft }}>Every</span>
          <input type="text" inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value.replace(/[^0-9]/g, ''))} placeholder="4" style={{ ...inputStyle, width: 80, textAlign: 'center' }} />
          <span style={{ fontSize: 14, color: K.soft }}>months</span>
        </div>
      )}

      {funds.length > 0 && (
        <>
          <Label>Saving for it in (optional)</Label>
          <div className="flex" style={{ gap: 7, overflowX: 'auto', paddingBottom: 2 }}>
            <Chip on={!fundId} onClick={() => setFundId('')}>Not linked</Chip>
            {funds.map((f) => <Chip key={f.id} on={fundId === String(f.id)} onClick={() => setFundId(String(f.id))}>{(f.emoji ? f.emoji + ' ' : '') + f.name}</Chip>)}
          </div>
        </>
      )}

      {pm > 0 && due && (
        <Note tone={r && r.ready ? 'mint' : 'lil'}>
          {shownEmoji} That's <b style={{ fontWeight: 600 }}>{money(Math.round(pm * 100) / 100)} a month</b>
          {intervalOf(draft) ? '.' : ` until ${shortDate(due)}.`}
          {r && (r.ready
            ? ` Your ${r.fund.name} already has enough for ${due.toLocaleDateString('en-US', { month: 'long' })}!`
            : ` Your ${r.fund.name} has ${wholeMoney(r.has)}, so ${wholeMoney(r.toGo)} to go by ${shortDate(due)}.`)}
        </Note>
      )}
      {date && !due && <Help>That date has passed. Pick the next time it's due.</Help>}
      {error && <p style={{ margin: '10px 4px 0', fontSize: 13, color: K.pinkT }}>{error}</p>}

      <BlackButton onClick={submit}>{cost ? 'Save' : 'Add yearly cost'}</BlackButton>
      {onDelete && (confirmDelete ? (
        <div className="flex" style={{ gap: 8, marginTop: 10 }}>
          <button type="button" onClick={() => setConfirmDelete(false)} style={{ flex: 1, border: 0, borderRadius: 999, padding: 12, fontSize: 14, fontWeight: 600, background: K.field, color: K.soft, fontFamily: 'inherit' }}>Keep it</button>
          <button type="button" onClick={onDelete} style={{ flex: 1, border: 0, borderRadius: 999, padding: 12, fontSize: 14, fontWeight: 600, background: K.pink, color: K.pinkT, fontFamily: 'inherit' }}>Yes, delete</button>
        </div>
      ) : (
        <QuietButton color={K.pinkT} onClick={() => setConfirmDelete(true)}>Delete this cost</QuietButton>
      ))}
    </PlanSheet>
  );
}
